/**
 * specGate — GATE-1 for schema-first Gen-UI view specs
 * (Compositor × Entity OS runtime, roadmap #485 / G1 #486, PR-B).
 *
 * GATE-1 is the STRUCTURAL WHITELIST that every DETERMINISTIC view spec (compiled
 * from an EntityDefinition by the entitySpecCompilers) must pass before it can be
 * served to a caller. It is a "closed-world" gate: a view spec may ONLY
 *   - pull data from the two READ tools an entity exposes (`entity.<slug>.list` /
 *     `entity.<slug>.get`) — NO mutation tools, NO operator/platform tools,
 *   - carry structurally-known keys at every level (`.strict()` everywhere — an
 *     unknown key is a REJECT, so a free-form expression / code field is
 *     structurally IMPOSSIBLE),
 *   - reference node `type`s from a passed-in allowlist (the primitive registry),
 *   - reference entity FIELDS that are PUBLIC (declared on the definition) — never a
 *     `gc_*` generated column, never a non-declared / internal envelope column.
 *
 * WHY THE FIELD CHECK IS AGAINST `definition.fields` (snake_case), NOT the wire
 * (camelCase) projection: the deterministic compilers emit SPEC-LEVEL keys as the
 * snake_case DEFINITION FIELD NAMES (a Table column `key` is `serial_no`, an
 * interpolation is `{{serial_no}}`); the render container bridges these to the
 * camelCase wire rows via `snakeToCamel` at render time (gh#1192, EntityList). So the
 * ONLY set that aligns with spec-level keys is `definition.fields`. This is also the
 * TIGHTEST safe surface: `definition.fields ⊆ publicColumns` (every declared field is
 * a public, non-`gc_` column), so "referenced field ∈ definition.fields" ⟹ "referenced
 * field ∈ public projection" — internal columns (tenant_id / owner_id / timestamps)
 * and `gc_*` generated columns can never be surfaced.
 *
 * NO DEEP WHERE VALIDATION: `dataSource.args.where` is accepted as opaque
 * (`z.unknown()`). The server-side E1 DSL compiler is fail-closed and is the SINGLE
 * authority on `where`; re-validating it here would be a double-validate (the Express
 * double-validate lesson) and could drift from the real DSL. GATE-1 only constrains
 * the SHAPE around it (whitelisted keys, bounded `limit`).
 *
 * Pure + deterministic: no LLM, no network, no clock. Shared has no dependency on
 * `@elmes/primitives`, so the node-type allowlist is PASSED IN by the caller
 * (the api registry passes `listPrimitiveMeta()` names).
 */
import { z } from 'zod';
import { dataSourceFilterSchema } from './windowSpec.js';
/**
 * G1 tool whitelist regex — a view dataSource may ONLY read through an entity's
 * `list` / `get` tool. The slug segment allows camelCase (`entity.designSystem.get`).
 * Mutation tools (`entity.<slug>.create|update|delete`), operator/platform tools
 * (`platform.list_users`), and domain tools (an extension's own, e.g. `notify.send`) are DELIBERATELY out.
 */
export const G1_TOOL_REGEX = /^entity\.[a-zA-Z0-9_]+\.(list|get)$/;
/** The `gc_` prefix that marks a generated (virtual) column — never referenceable. */
export const GC_COLUMN_PREFIX = 'gc_';
/**
 * GATE-1 strict filter schema — the `.strict()` variant of the base dataSourceFilter
 * (an unknown key is a REJECT here, not a silent STRIP as the loose base does). The
 * loose base stays in windowSpec.ts for the LLM mutate path; GATE-1 tightens on this
 * side. `filter.field` is additionally field/gc_-scanned in the semantic walk.
 */
export const strictDataSourceFilterSchema = dataSourceFilterSchema.strict();
/**
 * GATE-1 strict node schema — the recursive structural envelope. Only the five known
 * WindowSpec keys are allowed at each node (`.strict()`); `props` stays an opaque
 * record (the primitive owns its own props schema at render time — validating them
 * here would be a double-validate), but NO unknown TOP-LEVEL node key can appear, so a
 * spec can never smuggle a handler / expression / code field structurally. Declared
 * BEFORE the dataSource schema so `dataSource.itemTemplate` can reference it directly;
 * `dataSource` is referenced back through `z.lazy` (mutual recursion).
 */
export const strictViewNodeSchema = z.lazy(() => z
    .object({
    type: z.string(),
    id: z.string().optional(),
    props: z.record(z.unknown()).optional(),
    children: z.array(strictViewNodeSchema).optional(),
    dataSource: z.lazy(() => strictDataSourceSchema).optional(),
})
    .strict());
/**
 * GATE-1 strict dataSource schema. Every key is whitelisted; `.strict()` at each
 * object level rejects any unknown key. `where` is opaque (server-side E1 owns it),
 * `limit` is bounded, `id` is a scalar (or a `{{id}}` render-time placeholder string).
 *
 * `itemTemplate` is NOT opaque (closed-world completeness, reviewer FIX-1): it is a row
 * WindowSpec template, so it is FULLY validated as a node — the strict node schema here
 * (structure + strict keys + nested tool regex) plus the semantic walk (node-type
 * allowlist + field/gc_ scanning) recurse into it. An allowlist-foreign node type or a
 * `gc_` reference inside itemTemplate is therefore rejected exactly like a top-level one.
 * `filter` is the `.strict()` variant.
 */
export const strictDataSourceSchema = z
    .object({
    tool: z.string().regex(G1_TOOL_REGEX, 'tool must be entity.<slug>.list | entity.<slug>.get'),
    args: z
        .object({
        // Opaque — server-side E1 DSL is the fail-closed authority (no double-validate).
        where: z.unknown().optional(),
        limit: z.number().int().min(1).max(100).optional(),
        // Scalar record id OR a `{{id}}` render-time placeholder (detail view).
        id: z.union([z.string(), z.number()]).optional(),
    })
        .strict()
        .optional(),
    mapTo: z.enum(['children', 'items', 'rows', 'record']).default('children'),
    itemTemplate: strictViewNodeSchema.optional(),
    filter: strictDataSourceFilterSchema.optional(),
})
    .strict();
// ─── Mustache field-reference scanning ───────────────────────────────────────
const MUSTACHE_RE = /\{\{\s*([^}]+?)\s*\}\}/g;
/**
 * Recursively collect every `{{path}}` interpolation reference found in string values
 * of an arbitrary JSON value (node props / itemTemplate). Returns the raw inner paths.
 */
function collectMustacheRefs(value, out) {
    if (typeof value === 'string') {
        let m;
        MUSTACHE_RE.lastIndex = 0;
        while ((m = MUSTACHE_RE.exec(value)) !== null)
            out.push(m[1].trim());
        return;
    }
    if (Array.isArray(value)) {
        for (const v of value)
            collectMustacheRefs(v, out);
        return;
    }
    if (value && typeof value === 'object') {
        for (const v of Object.values(value))
            collectMustacheRefs(v, out);
    }
}
/** Segments of a dotted interpolation path (e.g. `profile.role` → ['profile','role']). */
function pathSegments(path) {
    return path.split('.').map((s) => s.trim());
}
// ─── The gate ─────────────────────────────────────────────────────────────────
/**
 * Validate a compiled view spec against GATE-1.
 *
 * Steps (fail-fast, first violation wins):
 *   1. STRUCTURAL — strict recursive schema parse (unknown keys, bad tool regex,
 *      bad args shape → `schema_invalid`).
 *   2. NODE TYPE — every node `type` ∈ `allowedNodeTypes` (when provided).
 *   3. FIELD SURFACE — every Table `columns[].key` and every `{{path}}` root segment
 *      references a PUBLIC declared field; any `gc_`-prefixed reference (column key or
 *      ANY path segment) is an unconditional REJECT.
 *
 * @param spec       the compiled WindowSpec.
 * @param definition the owning EntityDefinition (its `fields` are the public surface).
 *                   Omit for a non-entity view — field checks are then skipped, but the
 *                   structural + node-type + `gc_` checks still run.
 * @param options    `allowedNodeTypes` — the primitive registry names.
 */
export function validateViewSpec(spec, definition, options = {}) {
    // 1. Structural strict parse.
    const parsed = strictViewNodeSchema.safeParse(spec);
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        return {
            ok: false,
            code: 'schema_invalid',
            message: issue ? `${issue.path.join('.') || '<root>'}: ${issue.message}` : 'invalid spec',
            path: issue?.path.join('.') || undefined,
        };
    }
    const allowedNodeTypes = options.allowedNodeTypes
        ? options.allowedNodeTypes instanceof Set
            ? options.allowedNodeTypes
            : new Set(options.allowedNodeTypes)
        : undefined;
    const publicFields = definition ? new Set(definition.fields.map((f) => f.name)) : undefined;
    // 2 + 3. Semantic walk.
    const violation = walkNode(spec, { allowedNodeTypes, publicFields });
    if (violation)
        return violation;
    return { ok: true, spec };
}
function walkNode(node, ctx) {
    const nodePath = node.id ?? node.type;
    // 2. Node type allowlist.
    if (ctx.allowedNodeTypes && !ctx.allowedNodeTypes.has(node.type)) {
        return {
            ok: false,
            code: 'unknown_node_type',
            message: `node type '${node.type}' is not a registered primitive`,
            path: nodePath,
        };
    }
    const props = node.props;
    // 3a. Table (or any) `columns[].key` field references.
    if (props && Array.isArray(props.columns)) {
        const columns = props.columns;
        for (const col of columns) {
            if (!col || typeof col !== 'object')
                continue;
            const key = col.key;
            if (typeof key !== 'string')
                continue;
            const fieldViolation = checkFieldRef(key, ctx.publicFields, `${nodePath}.columns`);
            if (fieldViolation)
                return fieldViolation;
        }
    }
    // 3b. `{{path}}` interpolation references in props (Row.value, etc.). NOTE: dataSource
    // is walked separately below — its `args` are NOT field references (an `{{id}}`
    // placeholder is a dataSource arg, not a column) so they are deliberately not scanned.
    if (props) {
        const refs = [];
        collectMustacheRefs(props, refs);
        for (const ref of refs) {
            const rootViolation = checkPathRef(ref, ctx.publicFields, nodePath);
            if (rootViolation)
                return rootViolation;
        }
    }
    // 3c. dataSource.filter.field — root field / gc_ scan (reviewer FIX-2). The strict
    // filter schema already rejected unknown keys structurally; here the FIELD path is
    // held to the same public-field / no-gc_ surface as every other field reference.
    if (node.dataSource?.filter) {
        const filterViolation = checkPathRef(node.dataSource.filter.field, ctx.publicFields, `${nodePath}.filter`);
        if (filterViolation)
            return filterViolation;
    }
    // 3d. dataSource.itemTemplate — a row WindowSpec template, validated as a FULL node
    // (reviewer FIX-1: no opaque subtree). Recursing walkNode applies the node-type
    // allowlist + columns + {{path}} + filter + gc_/field checks to the template exactly
    // as to a top-level node. Structure was already strict-parsed (strictViewNodeSchema),
    // so the cast is safe (the walk only runs after a successful parse).
    if (node.dataSource?.itemTemplate !== undefined) {
        const tmplViolation = walkNode(node.dataSource.itemTemplate, ctx);
        if (tmplViolation)
            return tmplViolation;
    }
    // Recurse into children.
    if (node.children) {
        for (const child of node.children) {
            const childViolation = walkNode(child, ctx);
            if (childViolation)
                return childViolation;
        }
    }
    return undefined;
}
/** Check a single column key: `gc_` → reject; not a public field → reject. */
function checkFieldRef(key, publicFields, path) {
    if (key.startsWith(GC_COLUMN_PREFIX)) {
        return { ok: false, code: 'gc_reference', message: `generated column '${key}' is never referenceable`, path };
    }
    if (publicFields && !publicFields.has(key)) {
        return { ok: false, code: 'field_not_public', message: `field '${key}' is not a public entity field`, path };
    }
    return undefined;
}
/** Check a `{{path}}` interpolation: any `gc_` segment → reject; unknown root → reject. */
function checkPathRef(ref, publicFields, path) {
    const segments = pathSegments(ref);
    // Unconditional gc_ reject on ANY segment (defense-in-depth, gh gc_ prefix rule).
    for (const seg of segments) {
        if (seg.startsWith(GC_COLUMN_PREFIX)) {
            return { ok: false, code: 'gc_reference', message: `generated column '${seg}' is never referenceable`, path };
        }
    }
    const root = segments[0];
    if (root && publicFields && !publicFields.has(root)) {
        return { ok: false, code: 'field_not_public', message: `field '${root}' is not a public entity field`, path };
    }
    return undefined;
}
//# sourceMappingURL=specGate.js.map